import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, UserCheck, CalendarClock, ChevronRight } from "lucide-react";

export default function AdminStaffReserve() {
  const [email, setEmail] = useState("");
  const [member, setMember] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [selected, setSelected] = useState(null);
  const [svc, setSvc] = useState(null);
  const [avail, setAvail] = useState(null);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    resource: "", start_hour: null, duration: 1,
  });
  const [busy, setBusy] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    setMember(null); setMemberships([]); setSelected(null); setSvc(null); setAvail(null);
    try {
      const { data } = await api.get("/admin/members");
      const found = data.members.find((m) => m.email.toLowerCase() === email.toLowerCase().trim());
      if (!found) { toast.error("No member with that email"); return; }
      setMember(found);
      setMemberships(found.memberships || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Search failed"); }
  };

  useEffect(() => {
    if (!selected) return;
    api.get(`/services/${selected.facility_slug}`)
      .then((r) => { setSvc(r.data); setForm((f) => ({ ...f, resource: (r.data.resources || [])[0] || "" })); })
      .catch(() => toast.error("Facility unavailable"));
  }, [selected]);

  useEffect(() => {
    if (!svc || !form.date) return;
    api.get(`/availability?service_slug=${selected.facility_slug}&date=${form.date}`)
      .then((r) => setAvail(r.data)).catch(() => setAvail(null));
  }, [svc, form.date, selected]);

  const busyHours = new Set(
    avail && form.resource && avail.matrix[form.resource]
      ? Object.entries(avail.matrix[form.resource]).filter(([, v]) => v).map(([h]) => parseInt(h)) : []
  );

  const submit = async () => {
    if (form.start_hour == null) { toast.error("Pick an hour"); return; }
    setBusy(true);
    try {
      await api.post("/admin/staff-reserve", {
        member_email: email, membership_id: selected.id,
        resource: form.resource, date: form.date,
        start_hour: form.start_hour, duration: form.duration,
      });
      toast.success("Slot reserved for member — booking recorded");
      setForm({ ...form, start_hour: null });
      // refresh availability
      const { data } = await api.get(`/availability?service_slug=${selected.facility_slug}&date=${form.date}`);
      setAvail(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Reservation failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-5 md:p-8" data-testid="admin-staff-reserve">
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-widest brand-orange">// Desk Reservation</div>
        <h1 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tighter mt-1">Reserve for a Walk-in Member</h1>
        <p className="text-sm text-white/60 mt-1">Look up a member by email, pick their active membership, book their slot.</p>
      </div>

      {/* 1. member search */}
      <form onSubmit={search} className="border border-white/12 bg-[#131A2B] p-5 mb-4 flex gap-2" data-testid="member-search">
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="member@example.com"
          data-testid="staff-reserve-email"
          className="flex-1 bg-black/40 border border-white/15 px-3 py-2.5 text-sm" />
        <button type="submit" className="bg-brand-blue text-white px-5 py-2.5 font-bold text-xs uppercase tracking-widest inline-flex items-center gap-2">
          <Search size={12} /> Find Member
        </button>
      </form>

      {member && (
        <div className="border border-brand-blue/40 bg-brand-blue/8 p-4 mb-4 flex gap-3 items-start" data-testid="found-member">
          <UserCheck size={18} className="brand-blue mt-0.5" />
          <div>
            <div className="font-display font-black uppercase">{member.name}</div>
            <div className="text-xs text-white/60">{member.email} · {member.phone || "no phone"}</div>
          </div>
        </div>
      )}

      {/* 2. pick membership */}
      {memberships.length > 0 && (
        <div className="mb-4" data-testid="pick-membership">
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">// Pick an active membership</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {memberships.map((m) => (
              <button key={m.id} onClick={() => { setSelected(m); setForm({ ...form, start_hour: null }); }}
                disabled={!m.is_active}
                data-testid={`pick-membership-${m.id}`}
                className={`text-left border p-4 transition ${
                  selected?.id === m.id ? "border-brand-orange bg-brand-orange/8" :
                  m.is_active ? "border-white/15 hover:border-white/40" : "border-white/8 opacity-40 cursor-not-allowed"
                }`}>
                <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">{m.facility_name || m.facility_slug}</div>
                <div className="font-display font-black uppercase text-sm mt-0.5">{m.plan_snapshot?.name}</div>
                <div className="text-[11px] font-mono text-white/60 mt-1.5">
                  {m.quota_remaining == null ? "Unlimited" : `${m.quota_remaining} left`}
                  {m.expires_in_days != null && ` · expires in ${m.expires_in_days}d`}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 3. slot picker */}
      {selected && svc && (
        <div className="border border-white/12 bg-[#131A2B] p-5" data-testid="slot-picker">
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-3">// Slot</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value, start_hour: null })}
                min={new Date().toISOString().slice(0, 10)}
                className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Resource</label>
              <select value={form.resource} onChange={(e) => setForm({ ...form, resource: e.target.value })}
                className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm">
                {(svc.resources || []).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Available hours</label>
            <div className="flex flex-wrap gap-1.5">
              {(avail?.hours || []).map((h) => {
                const isBusy = busyHours.has(h);
                const sel = form.start_hour === h;
                return (
                  <button key={h} disabled={isBusy}
                    onClick={() => setForm({ ...form, start_hour: h })}
                    data-testid={`sr-hour-${h}`}
                    className={`px-2.5 py-1.5 border text-xs font-mono transition ${
                      isBusy ? "border-white/8 text-white/25 line-through cursor-not-allowed" :
                      sel ? "border-brand-orange bg-brand-orange text-black" : "border-white/20 hover:border-brand-blue"
                    }`}>{h}:00</button>
                );
              })}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Duration</label>
              <select value={form.duration} onChange={(e) => setForm({ ...form, duration: Number(e.target.value) })}
                className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm">
                {[1,2,3].map((d) => <option key={d} value={d}>{d}h</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button onClick={submit} disabled={busy}
                data-testid="staff-reserve-submit"
                className="w-full bg-brand-orange text-black py-2.5 font-bold text-xs uppercase tracking-widest hover:bg-white disabled:opacity-40 inline-flex items-center justify-center gap-2">
                <CalendarClock size={12} /> {busy ? "Reserving…" : "Reserve"} <ChevronRight size={12} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
